import {
  Bell,
  ChevronDown,
  ChevronUp,
  Pause,
  Play,
  Timer as TimerIcon,
  X,
} from "lucide-react"
import { useEffect, useRef, useState } from "react"

import { Button } from "@/components/ui/button"
import { type Timer } from "@/hooks/use-timers"
import { formatSeconds } from "@/lib/duration"
import { timerColor } from "@/lib/timer-colors"
import { cn } from "@/lib/utils"

interface TimersPanelProps {
  timers: Timer[]
  onPause: (id: string) => void
  onResume: (id: string) => void
  onCancel: (id: string) => void
  // Raises the panel above a page's fixed bottom navigation (e.g. Cooking Mode's
  // Previous/Next footer) so it doesn't cover the controls.
  liftForBottomNav?: boolean
  // In the native app the tab bar (and Cooking Mode's footer) sit inside the
  // bottom safe-area inset, so the panel is offset by env(safe-area-inset-bottom)
  // to stay above them. No-op on the web, where there is no tab bar.
  nativeApp?: boolean
}

// Groups timers by the recipe they belong to, preserving start order.
function groupByRecipe(timers: Timer[]) {
  const groups = new Map<number | undefined, Timer[]>()
  for (const timer of timers) {
    const existing = groups.get(timer.recipeId)
    if (existing) {
      existing.push(timer)
    } else {
      groups.set(timer.recipeId, [timer])
    }
  }
  return [...groups.values()]
}

// Floating, collapsible list of running timers. A single compact card with a
// summary header keeps the recipe steps readable on a phone even with several
// timers going. Sits above Cooking Mode's z-50 overlay. Renders nothing when empty.
export function TimersPanel({
  timers,
  onPause,
  onResume,
  onCancel,
  liftForBottomNav = false,
  nativeApp = false,
}: TimersPanelProps) {
  const [collapsed, setCollapsed] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)

  // Publish the drawer's on-screen footprint (its top edge to the viewport
  // bottom) as a CSS variable so scroll containers can reserve exactly that
  // much bottom padding and never let the drawer cover content. Resets to 0
  // when there are no timers (the panel renders nothing).
  useEffect(() => {
    const root = document.documentElement
    const el = panelRef.current
    if (!el) {
      root.style.setProperty("--timers-drawer-space", "0px")
      return
    }
    const update = () => {
      const rect = el.getBoundingClientRect()
      const space = Math.max(0, window.innerHeight - rect.top)
      root.style.setProperty("--timers-drawer-space", `${space}px`)
    }
    update()
    const observer = new ResizeObserver(update)
    observer.observe(el)
    window.addEventListener("resize", update)
    return () => {
      observer.disconnect()
      window.removeEventListener("resize", update)
      root.style.setProperty("--timers-drawer-space", "0px")
    }
  }, [timers.length, collapsed])

  if (timers.length === 0) return null

  const doneCount = timers.filter((t) => t.status === "done").length
  const soonest = timers
    .filter((t) => t.status === "running")
    .reduce<
      number | null
    >((min, t) => (min === null ? t.remainingSeconds : Math.min(min, t.remainingSeconds)), null)

  const summary =
    doneCount > 0
      ? `${doneCount} done`
      : soonest !== null
        ? `next ${formatSeconds(soonest)}`
        : "paused"

  // Only show recipe titles when timers span more than one recipe.
  const groups = groupByRecipe(timers)
  const showTitles = groups.length > 1

  function renderRow(timer: Timer) {
    const done = timer.status === "done"
    return (
      <li
        key={timer.id}
        className={cn(
          "flex items-center gap-2 px-3 py-2",
          done && "bg-amber-50 dark:bg-amber-950/40",
        )}
      >
        {timer.step !== undefined ? (
          <span
            className={cn(
              "flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-medium text-white",
              timerColor(timer.colorIndex).dot,
            )}
          >
            {timer.step}
          </span>
        ) : (
          <span
            className={cn(
              "size-2.5 shrink-0 rounded-full",
              timerColor(timer.colorIndex).dot,
            )}
          />
        )}

        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-medium">{timer.label}</p>
          {timer.context && (
            <p className="text-muted-foreground truncate text-[11px]">
              {timer.context}
            </p>
          )}
        </div>

        <span className="shrink-0 text-lg font-semibold tabular-nums">
          {done ? "Done" : formatSeconds(timer.remainingSeconds)}
        </span>

        {!done &&
          (timer.status === "running" ? (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => onPause(timer.id)}
              aria-label="Pause timer"
            >
              <Pause className="size-3.5" />
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => onResume(timer.id)}
              aria-label="Resume timer"
            >
              <Play className="size-3.5" />
            </Button>
          ))}

        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => onCancel(timer.id)}
          aria-label={done ? "Dismiss timer" : "Cancel timer"}
        >
          <X className="size-3.5" />
        </Button>
      </li>
    )
  }

  return (
    <div
      ref={panelRef}
      className={cn(
        "fixed inset-x-0 z-[60] p-3 sm:inset-x-auto sm:right-4 sm:w-72",
        nativeApp
          ? liftForBottomNav
            ? // Clear the tab bar (safe-area inset) AND Cooking Mode's footer.
              "bottom-[calc(env(safe-area-inset-bottom)_+_4.5rem)]"
            : // Sit just above the native tab bar.
              "bottom-[calc(env(safe-area-inset-bottom)_+_0.75rem)]"
          : liftForBottomNav
            ? "bottom-24 sm:bottom-24"
            : "bottom-0 sm:bottom-4",
      )}
    >
      <div className="bg-popover text-popover-foreground overflow-hidden rounded-lg shadow-lg ring-1 ring-black/5 dark:ring-white/20">
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
          className="hover:bg-accent/50 flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium"
        >
          <TimerIcon className="size-4 shrink-0" />
          <span className="flex-1 truncate">
            {timers.length} timer{timers.length > 1 ? "s" : ""} · {summary}
          </span>
          {doneCount > 0 && <Bell className="size-4 shrink-0 text-amber-500" />}
          {collapsed ? (
            <ChevronUp className="size-4 shrink-0" />
          ) : (
            <ChevronDown className="size-4 shrink-0" />
          )}
        </button>

        {!collapsed && (
          <div className="max-h-[40vh] overflow-y-auto sm:max-h-80">
            {groups.map((group) => (
              <div key={group[0].recipeId ?? "none"}>
                {showTitles && group[0].recipeTitle && (
                  <p className="bg-muted/50 text-muted-foreground truncate border-t px-3 py-1 text-[11px] font-medium tracking-wide uppercase">
                    {group[0].recipeTitle}
                  </p>
                )}
                <ul className="divide-y">{group.map(renderRow)}</ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
